import { useEffect, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { ImagePlus, SendHorizontal } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@helios/ui';
import { ConstructionOsLogo } from '@/components/brand/ConstructionOsLogo';
import type { AiChatMessage } from '@/features/ai-chat/aiChat.types';
import { useUiStore } from '@/stores/uiStore';

type ChatMessage = Pick<AiChatMessage, 'id' | 'role' | 'content'> &
  Partial<Pick<AiChatMessage, 'attachments' | 'toolCalls'>>;

/** One conversation window for platform chat and the restricted company setup context. */
export function HeliosChatWindow({
  open,
  onOpenChange,
  description,
  onReturnFocus,
  badge,
  appearance,
  children,
  ...dropZoneProps
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  description: string;
  onReturnFocus?: () => void;
  badge?: ReactNode;
  appearance?: 'setup';
  children: ReactNode;
} & Pick<HTMLAttributes<HTMLDivElement>, 'onDragOver' | 'onDragLeave' | 'onDrop'>) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="helios-chat-dropzone"
        className={`border-border-strong/40 bg-background/90 shadow-elevation-4 backdrop-blur-glass flex w-full max-w-full flex-col gap-0 border-l p-0 sm:max-w-[36rem] ${appearance === 'setup' ? 'helios-setup-dialog' : ''}`}
        onCloseAutoFocus={
          onReturnFocus
            ? (event) => {
                event.preventDefault();
                onReturnFocus();
              }
            : undefined
        }
        {...dropZoneProps}
      >
        <SheetHeader className="border-border/60 bg-card/[0.42] gap-0 border-b px-3.5 py-2.5">
          <div className="flex items-center justify-between gap-2 pr-9">
            <div className="flex min-w-0 items-center gap-2">
              <ConstructionOsLogo variant="toolbar" className="shrink-0" />
              <div className="min-w-0 leading-none">
                <SheetTitle className="font-display text-foreground text-[15px] font-semibold">
                  Helios AI
                </SheetTitle>
                <SheetDescription className="sr-only">{description}</SheetDescription>
              </div>
            </div>
            {badge}
          </div>
        </SheetHeader>
        {children}
      </SheetContent>
    </Sheet>
  );
}

export function HeliosMessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'USER';
  return (
    <article
      className={`flex flex-col gap-1 ${isUser ? 'items-end' : 'items-start'}`}
      aria-label={isUser ? 'Your message' : 'Helios reply'}
    >
      <div
        className={[
          'max-w-[88%] px-2.5 py-1.5 text-[13px] leading-snug',
          isUser
            ? 'border-primary/40 bg-primary/90 text-primary-foreground shadow-glow rounded-[1rem_1rem_0.25rem_1rem] border'
            : 'border-border/60 bg-card/[0.78] text-foreground shadow-elevation-1 backdrop-blur-glass rounded-[1rem_1rem_1rem_0.25rem] border',
        ].join(' ')}
      >
        <p className="whitespace-pre-wrap">{message.content}</p>
      </div>
      {(message.attachments?.length ?? 0) > 0 ? (
        <ul className="flex max-w-[88%] flex-wrap gap-1.5">
          {message.attachments?.map((attachment) => (
            <li key={attachment.id}>
              {attachment.mimeType.startsWith('image/') && attachment.downloadUrl ? (
                <a href={attachment.downloadUrl} target="_blank" rel="noreferrer">
                  <img
                    src={attachment.downloadUrl}
                    alt={attachment.fileName}
                    className="border-border/60 h-20 w-auto rounded-md border object-cover"
                  />
                </a>
              ) : attachment.downloadUrl ? (
                <a
                  href={attachment.downloadUrl}
                  className="border-border/50 text-muted-foreground hover:text-foreground inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px]"
                  target="_blank"
                  rel="noreferrer"
                >
                  {attachment.fileName}
                </a>
              ) : (
                <span className="text-muted-foreground text-[11px]">{attachment.fileName}</span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {(message.toolCalls?.length ?? 0) > 0 ? (
        <ul className="flex max-w-[88%] flex-col gap-1">
          {message.toolCalls?.map((tool) => (
            <li
              key={tool.id}
              className="border-brand/50 bg-muted/20 flex items-center justify-between gap-2 border-l-2 px-2 py-1 text-[11px]"
            >
              <span className="truncate font-medium">{tool.toolKey}</span>
              <span className="text-muted-foreground shrink-0">{tool.status}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

/** The text-and-send portion is identical in both contexts; capability tools stay scoped. */
export function HeliosChatComposerInput({
  value,
  onChange,
  onSend,
  disabled,
  sendDisabled,
  label,
  sendLabel = 'Send message',
  placeholder = 'Ask Helios…',
  maxLength,
  imageMode = false,
}: {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled: boolean;
  sendDisabled: boolean;
  label?: string;
  sendLabel?: string;
  placeholder?: string;
  maxLength?: number;
  imageMode?: boolean;
}) {
  return (
    <>
      <textarea
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={1}
        maxLength={maxLength}
        className="border-border/70 bg-background/60 placeholder:text-muted-foreground/70 focus-visible:ring-primary/40 max-h-28 min-h-[2rem] min-w-0 flex-1 resize-none rounded-md border px-2.5 py-1.5 text-[13px] leading-snug shadow-inner focus-visible:outline-none focus-visible:ring-1"
        disabled={disabled}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            if (!disabled && !sendDisabled) onSend();
          }
        }}
      />
      <button
        type="button"
        className="border-ai/45 bg-ai text-ai-foreground shadow-glow-ai mb-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border transition-opacity hover:opacity-90 disabled:opacity-40"
        disabled={disabled || sendDisabled}
        onClick={onSend}
        aria-label={sendLabel}
      >
        {imageMode ? (
          <ImagePlus className="h-3.5 w-3.5" />
        ) : (
          <SendHorizontal className="h-3.5 w-3.5" />
        )}
      </button>
    </>
  );
}

export interface HeliosSetupChatContext {
  scopeKey: string;
  companyName: string;
  stepLabel?: string;
  messages: ChatMessage[];
  busy: boolean;
  disabledReason: string | null;
  onSend: (message: string) => Promise<boolean>;
  onReturnFocus?: () => void;
  footer?: ReactNode;
}

/** No platform query, preference, voice or attachment hooks are mounted in setup mode. */
export function HeliosSetupChatPanel({ setup }: { setup: HeliosSetupChatContext }) {
  const open = useUiStore((state) => state.advisorOpen);
  const setOpen = useUiStore((state) => state.setAdvisorOpen);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [setup.messages.length, open]);
  const send = async () => {
    const message = draft.trim();
    if (inFlight.current || setup.busy || setup.disabledReason || message.length < 2) return;
    inFlight.current = true;
    setSending(true);
    try {
      const sent = await setup.onSend(message);
      if (mounted.current && sent) setDraft('');
    } finally {
      inFlight.current = false;
      if (mounted.current) setSending(false);
    }
  };
  return (
    <HeliosChatWindow
      open={open}
      onOpenChange={setOpen}
      appearance="setup"
      onReturnFocus={setup.onReturnFocus}
      description="Helios AI for guided company setup. Questions use only this company's saved setup."
      badge={
        <span className="border-brand/30 bg-brand/10 text-brand rounded-md border px-1.5 py-0.5 text-[10px]">
          Company setup
        </span>
      }
    >
      <div className="border-border/50 border-b px-4 py-3">
        <p className="text-foreground text-sm font-medium">{setup.companyName}</p>
        <p className="text-muted-foreground mt-1 text-xs">
          {setup.stepLabel ? `${setup.stepLabel} · ` : ''}Company settings and access guidance
        </p>
      </div>
      <div
        ref={scroller}
        className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4"
        aria-live="polite"
        aria-busy={setup.busy || sending}
      >
        {setup.messages.length === 0 ? (
          <div className="py-8">
            <ConstructionOsLogo variant="toolbar" className="mb-4" />
            <h2 className="text-foreground text-lg font-semibold">Let’s set up your workspace.</h2>
            <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
              Ask about company details, people, permissions, or connections. Suggested changes stay
              in review until you approve them.
            </p>
          </div>
        ) : (
          setup.messages.map((message) => (
            <HeliosMessageBubble key={message.id} message={message} />
          ))
        )}
        {setup.busy || sending ? (
          <p role="status" className="text-muted-foreground text-xs">
            Working on your setup question…
          </p>
        ) : null}
      </div>
      <div className="border-border/60 bg-card/50 backdrop-blur-glass border-t px-3 py-3">
        {setup.disabledReason ? (
          <p role="status" className="text-warning mb-2 text-xs">
            {setup.disabledReason}
          </p>
        ) : null}
        <div className="flex items-end gap-2">
          <HeliosChatComposerInput
            value={draft}
            onChange={setDraft}
            onSend={() => void send()}
            disabled={setup.busy || sending || Boolean(setup.disabledReason)}
            sendDisabled={draft.trim().length < 2}
            label="Ask about your setup"
            sendLabel="Ask assistant"
            placeholder="Ask about your setup…"
            maxLength={2000}
          />
        </div>
        {setup.footer}
      </div>
    </HeliosChatWindow>
  );
}
